'use client';
import { useState } from 'react';
import QuoteCard from '../components/QuoteCard';
import CategoryFilter from '../components/CategoryFilter';
import AuthorFilter from '../components/AuthorFilter';
import { useRandomQuote } from '../hooks/useRandomQuote';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { logAnalyticsEvent } from '@/lib/analytics';
import { Quote } from '@/data/quotesInterface';
import { useToast } from '@/hooks/use-toast';

const Index = ({ initialQuote, initialImage }: { initialQuote: Quote, initialImage: string }) => {
  const {
    quote,
    image,
    refreshQuote,
    selectedCategories,
    setSelectedCategories,
    selectedAuthors,
    setSelectedAuthors,
  } = useRandomQuote();

  const { toast } = useToast();

  const [shownQuote, setShownQuote] = useState<Quote>(initialQuote);
  const [shownImage, setShownImage] = useState<string>(initialImage);

  const refreshQuoteHandler = async () => {
    let toastDismiss: (() => void) | null = null;
    const longRequestTimeOutId = setTimeout(() => {
      toastDismiss = toast({
        title: "Loading...",
        description: "This is taking longer than usual. Please wait.",
      }).dismiss;
    }, 1000);
    const { quote: newQuote, image: newImage } = await refreshQuote();
    clearTimeout(longRequestTimeOutId);
    if (toastDismiss) {
      toastDismiss();
    }
    if (newQuote) {
      setShownQuote(newQuote);
      setShownImage(newImage);
    }
  };
  const [activeTab, setActiveTab] = useState<string>("quote");

  return (
    <div className="flex flex-col min-h-[calc(100vh-3.5rem)] bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
      <div className="flex-1 py-12 px-4 flex flex-col items-center">
        <div className="w-full max-w-6xl">
          <div className="text-center mb-10">
            <h1 className="text-2xl font-bold mb-3 bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
              Nature Photos and Sayings to Inspire You
            </h1>
            <div className="max-w-3xl mx-auto">
              <p className="text-gray-700 mb-4">
                Get quotes with beautiful nature photos to enhance your wisdom, motivation and inspire your growth. Our sayings collection spans love, motivational, inspirational, friendship, life quotes, and more.
              </p>
            </div>
          </div>

        </div>

        <main className="w-full max-w-6xl mb-auto">
          <Tabs defaultValue="quote" className="w-full" value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="quote"><h3>View Quote</h3></TabsTrigger>
              <TabsTrigger value="filters"><h3>Customize Filters</h3></TabsTrigger>
            </TabsList>

            <TabsContent value="quote" className="space-y-4">
              <QuoteCard quote={shownQuote} image={shownImage} onRefresh={async () => await refreshQuoteHandler()} />

              {!shownQuote && (
                <div className="text-center mt-4 text-amber-600 animate-pulse">
                  No quotes found for this combination of filters. Showing a random quote instead.
                </div>
              )}
            </TabsContent>

            <TabsContent value="filters" className="space-y-6 max-w-3xl mx-auto p-4 bg-white/50 rounded-lg shadow-sm">
              <h3 className="text-xl text-center text-green-800 font-medium mb-4">Customize Your Quote Experience</h3>

              <CategoryFilter
                selectedCategories={selectedCategories}
                onCategoriesChange={setSelectedCategories}
              />

              <AuthorFilter
                selectedAuthors={selectedAuthors}
                onAuthorsChange={setSelectedAuthors}
              />

              <div className="flex justify-center mt-6">
                <Button
                  onClick={async () => {
                    await refreshQuoteHandler();
                    setActiveTab("quote");
                    logAnalyticsEvent('apply_filter_click');
                  }}
                  className="bg-gradient-to-r from-green-600 to-blue-600 hover:opacity-90 text-white"
                >
                  Apply & View Quote
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </main>
      </div>

      {/* <Footer></Footer> */}
    </div>
  );
};

export default Index;
